import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  ArrowRight, 
  CheckCircle2, 
  HelpCircle, 
  Layers, 
  Cpu, 
  ShieldCheck, 
  Tv, 
  Send
} from 'lucide-react';
import { subservices as subservicesTr } from '../data/subservices';
import { subservicesEn } from '../data/subservices.en';
import { services as servicesTr } from '../data/services';
import { servicesEn } from '../data/services.en';
import { projects as projectsTr } from '../data/projects';
import { projectsEn } from '../data/projects.en';
import { useLanguage } from '../context/LanguageContext';
import { tr } from '../locales/tr';
import { en } from '../locales/en';

const SubServiceDetail: React.FC = () => {
  const { categorySlug, subserviceSlug } = useParams<{ categorySlug: string; subserviceSlug: string }>();
  const { language } = useLanguage();
  const t = language === 'TR' ? tr.services : en.services;
  
  const currentSubservices = language === 'TR' ? subservicesTr : subservicesEn;
  const currentServices = language === 'TR' ? servicesTr : servicesEn;
  const currentProjects = language === 'TR' ? projectsTr : projectsEn;

  // Look up current subservice
  const subservice = useMemo(() => {
    return currentSubservices.find(
      s => s.categorySlug === categorySlug && s.slug === subserviceSlug
    );
  }, [categorySlug, subserviceSlug, currentSubservices]);

  // Look up parent category
  const parentService = useMemo(() => {
    return currentServices.find(s => s.slug === categorySlug);
  }, [categorySlug, currentServices]);

  // Related projects matching this subservice or parent category
  const relatedProjects = useMemo(() => {
    if (!subservice) return [];
    if (subservice.relatedProjectSlugs && subservice.relatedProjectSlugs.length > 0) {
      const explicit = currentProjects.filter(p => subservice.relatedProjectSlugs?.includes(p.slug));
      if (explicit.length > 0) return explicit;
    }
    // Fallback: projects in the parent category
    return currentProjects
      .filter(p => parentService && p.categories.some(cat => cat.toLowerCase().includes(parentService.title.toLowerCase()) || parentService.title.toLowerCase().includes(cat.toLowerCase())))
      .slice(0, 3);
  }, [subservice, parentService, currentProjects]);

  // 404 state
  if (!subservice) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center text-center p-6">
        <Tv className="w-16 h-16 text-[#ff7a63]/30 mb-6" />
        <h1 className="text-3xl font-display text-white mb-2">{t.notFound}</h1>
        <p className="text-sm text-muted-foreground max-w-sm mb-8">
          {t.notFoundDesc}
        </p>
        <Link
          to={language === 'EN' ? "/en/services" : "/hizmetler"}
          className="px-6 py-3 bg-foreground text-background text-xs font-bold uppercase tracking-wider rounded-full hover:bg-foreground/90 transition-all shadow-md"
        >
          {t.backToList}
        </Link>
      </div>
    );
  }

  const categoryUrl = language === 'EN' 
    ? `/en/services/${categorySlug}` 
    : `/hizmetler/${categorySlug}`;
  const canonicalUrl = language === 'EN'
    ? `https://www.we24agency.com/en/services/${categorySlug}/${subserviceSlug}`
    : `https://www.we24agency.com/hizmetler/${categorySlug}/${subserviceSlug}`;
  const alternateTr = `https://www.we24agency.com/hizmetler/${categorySlug}/${subserviceSlug}`;
  const alternateEn = `https://www.we24agency.com/en/services/${categorySlug}/${subserviceSlug}`;

  return (
    <div className="bg-[#030811] text-foreground min-h-screen">
      <Helmet>
        <title>{`${subservice.metaTitle || subservice.title} - WE24 Agency`}</title>
        <meta name="description" content={subservice.metaDescription || subservice.shortDescription} />
        <meta name="keywords" content={(subservice.keywords || []).join(', ')} />
        <link rel="canonical" href={canonicalUrl} />
        <link rel="alternate" hrefLang="tr" href={alternateTr} />
        <link rel="alternate" hrefLang="en" href={alternateEn} />
        <link rel="alternate" hrefLang="x-default" href={alternateTr} />

        <meta property="og:title" content={`${subservice.title} - WE24 Agency`} />
        <meta property="og:description" content={subservice.metaDescription || subservice.shortDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="WE24 Agency" />

        {/* Structured Data: Service + BreadcrumbList + FAQPage */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "BreadcrumbList",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "position": 1,
                    "name": language === 'EN' ? "Home" : "Ana Sayfa",
                    "item": language === 'EN' ? "https://www.we24agency.com/en" : "https://www.we24agency.com/"
                  },
                  {
                    "@type": "ListItem",
                    "position": 2,
                    "name": language === 'EN' ? "Services" : "Hizmetlerimiz",
                    "item": language === 'EN' ? "https://www.we24agency.com/en/services" : "https://www.we24agency.com/hizmetler"
                  },
                  {
                    "@type": "ListItem",
                    "position": 3,
                    "name": subservice.categoryTitle,
                    "item": language === 'EN' ? `https://www.we24agency.com/en/services/${categorySlug}` : `https://www.we24agency.com/hizmetler/${categorySlug}`
                  },
                  {
                    "@type": "ListItem",
                    "position": 4,
                    "name": subservice.title,
                    "item": canonicalUrl
                  }
                ]
              },
              {
                "@type": "Service",
                "name": subservice.title,
                "description": subservice.shortDescription,
                "provider": {
                  "@type": "Organization",
                  "name": "WE24 Agency",
                  "url": "https://www.we24agency.com",
                  "telephone": "+90 545 229 24 24",
                  "email": "info@we24agency.com",
                  "address": {
                    "@type": "PostalAddress",
                    "streetAddress": "Meşrutiyet Mah. Şehit İsmet Armağan Sok. No: 7",
                    "addressLocality": "Nişantaşı, Şişli",
                    "addressRegion": "İstanbul",
                    "postalCode": "34363",
                    "addressCountry": "TR"
                  }
                }
              },
              ...(subservice.faqs && subservice.faqs.length > 0 ? [{
                "@type": "FAQPage",
                "mainEntity": subservice.faqs.map(faq => ({
                  "@type": "Question",
                  "name": faq.question,
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": faq.answer
                  }
                }))
              }] : [])
            ]
          })}
        </script>
      </Helmet>

      {/* 1. Hero Section & Breadcrumb Navigation */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-24 border-b border-white/[0.08] overflow-hidden bg-gradient-to-b from-[#05131f] via-[#030811] to-[#030811]">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[350px] bg-[#ff7a63]/10 blur-[140px] rounded-full pointer-events-none" />

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          {/* Breadcrumbs */}
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-xs text-zinc-400 mb-8 flex-wrap">
            <Link to={language === 'EN' ? "/en" : "/"} className="hover:text-white transition-colors">
              {language === 'EN' ? "Home" : "Ana Sayfa"}
            </Link>
            <span>/</span>
            <Link to={language === 'EN' ? "/en/services" : "/hizmetler"} className="hover:text-white transition-colors">
              {language === 'EN' ? "Services" : "Hizmetlerimiz"}
            </Link>
            <span>/</span>
            <Link to={categoryUrl} className="text-[#ff7a63] hover:underline transition-colors font-medium">
              {subservice.categoryTitle}
            </Link>
            <span>/</span>
            <span className="text-white font-medium truncate max-w-xs">{subservice.title}</span>
          </nav>

          {/* Subservice Badge & Header */}
          <div className="flex items-center gap-2.5 mb-4">
            <span className="text-xs uppercase font-bold tracking-widest text-[#ff7a63] bg-[#ff7a63]/10 border border-[#ff7a63]/25 px-3.5 py-1 rounded-full">
              {subservice.categoryTitle}
            </span>
          </div>

          <h1 className="text-3xl sm:text-5xl md:text-6xl font-display text-white tracking-tight leading-[1.1] max-w-5xl mb-6">
            {subservice.title}
          </h1>

          <p className="text-base sm:text-xl text-zinc-300 font-light leading-relaxed max-w-3xl border-l-2 border-[#ff7a63] pl-5 py-1">
            {subservice.heroTagline || subservice.shortDescription}
          </p>

          <div className="flex flex-wrap items-center gap-4 mt-8">
            <Link
              to={language === 'EN' ? "/en/contact" : "/iletisim"}
              className="px-8 py-3.5 bg-[#ff7a63] hover:bg-[#ff7a63]/90 text-white text-xs sm:text-sm font-bold uppercase tracking-wider rounded-full transition-all shadow-lg inline-flex items-center gap-2"
            >
              <Send className="w-4 h-4" />
              <span>{language === 'EN' ? "Request Proposal" : "Teklif ve Bilgi Alın"}</span>
            </Link>
            <Link
              to={categoryUrl}
              className="px-6 py-3.5 bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-white text-xs sm:text-sm font-semibold rounded-full transition-all inline-flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>{language === 'EN' ? `Back to ${subservice.categoryTitle}` : `${subservice.categoryTitle} Kategorisine Dön`}</span>
            </Link>
          </div>
        </div>
      </section>

      {/* 2. Value Proposition & Full Overview */}
      <section className="py-16 md:py-20 border-b border-white/[0.08] bg-[#040e18]">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          
          <div className="lg:col-span-8 flex flex-col gap-10">
            <div>
              <span className="text-xs font-bold uppercase tracking-widest text-[#ff7a63] mb-2 block">
                {language === 'EN' ? "EXECUTIVE OVERVIEW" : "GENEL BAKIŞ & KAPSAM"}
              </span>
              <h2 className="text-2xl sm:text-3xl font-display text-white mb-6">
                {language === 'EN' ? `Professional ${subservice.title} Solutions` : `Profesyonel ${subservice.title} Çözümlerimiz`}
              </h2>
              <p className="text-base text-zinc-300 leading-relaxed font-light">
                {subservice.fullDescription}
              </p>
            </div>

            {/* Why Us Cards */}
            {subservice.whyUs && subservice.whyUs.length > 0 && (
              <div className="bg-card/20 border border-white/[0.08] rounded-3xl p-6 sm:p-8">
                <div className="flex items-center gap-2.5 text-xs font-bold uppercase tracking-widest text-[#ff7a63] mb-6">
                  <ShieldCheck className="w-4 h-4" />
                  <span>{language === 'EN' ? "Why Partner With WE24 Agency?" : "Neden WE24 Agency ile Çalışmalısınız?"}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {subservice.whyUs.map((reason, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-4 bg-white/[0.02] border border-white/[0.05] rounded-2xl">
                      <CheckCircle2 className="w-4 h-4 text-[#ff7a63] shrink-0 mt-0.5" />
                      <span className="text-xs sm:text-sm text-zinc-200 leading-relaxed font-normal">{reason}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* In-depth Article / Authority Guide */}
            {subservice.articleSections && subservice.articleSections.map((sec, idx) => (
              <article key={idx} className="bg-card/15 border border-white/[0.06] rounded-3xl p-6 sm:p-8">
                <h3 className="text-xl sm:text-2xl font-display text-white mb-4">
                  {sec.heading}
                </h3>
                <p className="text-sm sm:text-base text-zinc-300 leading-relaxed mb-6 font-light">
                  {sec.content}
                </p>
                {sec.subPoints && (
                  <div className="grid grid-cols-1 gap-2.5 pt-4 border-t border-white/[0.06]">
                    {sec.subPoints.map((pt, pIdx) => (
                      <div key={pIdx} className="flex items-center gap-2.5 text-xs sm:text-sm text-zinc-200">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#ff7a63]" />
                        <span>{pt}</span>
                      </div>
                    ))}
                  </div>
                )}
              </article>
            ))}

            {/* Step-by-Step Workflow */}
            {subservice.process && subservice.process.length > 0 && (
              <div className="bg-card/20 border border-white/[0.08] rounded-3xl p-6 sm:p-8">
                <div className="flex items-center gap-2.5 text-xs font-bold uppercase tracking-widest text-[#ff7a63] mb-6">
                  <Layers className="w-4 h-4" />
                  <span>{language === 'EN' ? "Workflow & Execution Steps" : "Uygulama ve İş Akışı Süreci"}</span>
                </div>
                <div className="flex flex-col gap-4">
                  {subservice.process.map((step, idx) => (
                    <div key={idx} className="flex items-start gap-4 p-4 bg-white/[0.02] border border-white/[0.04] rounded-2xl">
                      <div className="w-7 h-7 rounded-full bg-[#ff7a63]/20 border border-[#ff7a63]/40 flex items-center justify-center font-bold text-[#ff7a63] text-xs shrink-0 mt-0.5">
                        {idx + 1}
                      </div>
                      <p className="text-xs sm:text-sm text-zinc-200 leading-relaxed">{step}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* FAQs with Accordion / Cards */}
            {subservice.faqs && subservice.faqs.length > 0 && (
              <div className="bg-card/20 border border-white/[0.08] rounded-3xl p-6 sm:p-8">
                <div className="flex items-center gap-2.5 text-xs font-bold uppercase tracking-widest text-[#ff7a63] mb-6">
                  <HelpCircle className="w-4 h-4" />
                  <span>{language === 'EN' ? "Frequently Asked Questions" : "Sıkça Sorulan Sorular"}</span>
                </div>
                <div className="flex flex-col gap-4">
                  {subservice.faqs.map((faq, idx) => (
                    <div key={idx} className="p-5 bg-white/[0.02] border border-white/[0.05] rounded-2xl">
                      <h4 className="text-sm sm:text-base font-semibold text-white mb-2">{faq.question}</h4>
                      <p className="text-xs sm:text-sm text-zinc-300 leading-relaxed font-light">{faq.answer}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>

          {/* Sidebar (4 Columns): Deliverables, Tech Specs, Contact CTA */}
          <div className="lg:col-span-4 flex flex-col gap-8">
            
            {/* Deliverables Card */}
            {subservice.deliverables && subservice.deliverables.length > 0 && (
              <div className="bg-card/25 border border-white/[0.08] p-6 sm:p-7 rounded-3xl flex flex-col gap-5">
                <h3 className="text-xs font-bold uppercase tracking-widest text-white border-b border-white/[0.08] pb-3 flex items-center gap-2">
                  <Layers className="w-4 h-4 text-[#ff7a63]" />
                  <span>{language === 'EN' ? "Deliverables & Formats" : "Teslim Edilen Çıktılar"}</span>
                </h3>
                <div className="flex flex-col gap-3">
                  {subservice.deliverables.map((item, idx) => (
                    <div key={idx} className="flex items-start gap-2.5 text-xs text-zinc-200">
                      <CheckCircle2 className="w-3.5 h-3.5 text-[#ff7a63] shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Technical Specifications */}
            {subservice.techSpecs && subservice.techSpecs.length > 0 && (
              <div className="bg-card/25 border border-white/[0.08] p-6 sm:p-7 rounded-3xl flex flex-col gap-5">
                <h3 className="text-xs font-bold uppercase tracking-widest text-white border-b border-white/[0.08] pb-3 flex items-center gap-2">
                  <Cpu className="w-4 h-4 text-[#ff7a63]" />
                  <span>{language === 'EN' ? "Technical Standards" : "Teknoloji Standartları"}</span>
                </h3>
                <div className="flex flex-col gap-4">
                  {subservice.techSpecs.map((spec, idx) => (
                    <div key={idx} className="flex flex-col gap-1.5">
                      <span className="text-[11px] font-bold text-[#ff7a63] uppercase">{spec.category}</span>
                      <div className="flex flex-wrap gap-1.5">
                        {spec.items.map((item, iIdx) => (
                          <span key={iIdx} className="text-[11px] bg-white/[0.04] border border-white/[0.08] px-2.5 py-1 rounded-md text-zinc-300">
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Consultation CTA */}
            <div className="bg-gradient-to-br from-[#ff7a63]/20 via-card/30 to-card/10 border border-[#ff7a63]/30 p-6 sm:p-7 rounded-3xl flex flex-col gap-4 text-center">
              <h4 className="text-base font-display text-white font-semibold">
                {language === 'EN' ? `Ready to Start Your ${subservice.title} Project?` : `${subservice.title} Projenizi Başlatalım`}
              </h4>
              <p className="text-xs text-zinc-300 leading-relaxed">
                {language === 'EN' 
                  ? "Contact our Nişantaşı studio team to discuss timelines, technical scope, and receive a tailored quote."
                  : "Nişantaşı stüdyomuzla iletişime geçerek projenizin kapsamını, takvimini ve teklifini planlayalım."}
              </p>
              <Link
                to={language === 'EN' ? "/en/contact" : "/iletisim"}
                className="mt-2 w-full py-3 bg-[#ff7a63] hover:bg-[#ff7a63]/90 text-white text-xs font-bold uppercase tracking-wider rounded-full transition-all shadow-lg flex items-center justify-center gap-2"
              >
                <span>{language === 'EN' ? "Get in Touch" : "Hemen Teklif Alın"}</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>

          </div>

        </div>
      </section>

      {/* 3. Related Portfolio Showcase */}
      {relatedProjects.length > 0 && (
        <section className="py-20 md:py-24 bg-[#030811] border-t border-white/[0.08]">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-center justify-between mb-10">
              <div>
                <span className="text-xs font-bold uppercase tracking-widest text-[#ff7a63] block mb-1">
                  {language === 'EN' ? "SELECTED WORK" : "SEÇİLİ VAKA ÇALIŞMALARI"}
                </span>
                <h2 className="text-2xl sm:text-4xl font-display text-white">
                  {language === 'EN' ? `Related ${subservice.title} Projects` : `İlgili ${subservice.title} Projeleri`}
                </h2>
              </div>
              <Link
                to={language === 'EN' ? "/en/projects" : "/projeler"}
                className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white font-medium transition-colors"
              >
                <span>{language === 'EN' ? "All Projects" : "Tüm Projeler"}</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
              {relatedProjects.map(p => {
                const pUrl = language === 'EN' ? `/en/projects/${p.slug}` : `/projeler/${p.slug}`;
                return (
                  <div key={p.id} className="flex flex-col gap-4 group bg-card/20 border border-white/[0.06] hover:border-[#ff7a63]/40 rounded-3xl p-4 transition-all duration-300 hover:-translate-y-1">
                    <Link to={pUrl} className="relative overflow-hidden aspect-video bg-zinc-900 rounded-2xl border border-white/10">
                      <img 
                        src={p.coverImage} 
                        alt={p.title} 
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" 
                        loading="lazy" 
                      />
                    </Link>
                    <div className="flex flex-col flex-grow justify-between">
                      <div>
                        <span className="text-[10px] uppercase font-bold tracking-widest text-[#ff7a63] mb-1.5 block">
                          {p.categories[0]} {p.year ? `· ${p.year}` : ''}
                        </span>
                        <Link to={pUrl}>
                          <h3 className="text-base sm:text-lg font-display text-white group-hover:text-[#ff7a63] transition-colors leading-snug font-medium mb-2 line-clamp-2">
                            {p.title}
                          </h3>
                        </Link>
                        <p className="text-xs text-zinc-300 leading-relaxed line-clamp-2 mb-3">
                          {p.strategicImpact || p.shortDescription}
                        </p>
                      </div>
                      <div className="pt-3 border-t border-white/[0.06] flex items-center justify-between text-xs text-zinc-400">
                        <span className="truncate max-w-[150px] font-medium text-zinc-400">{p.client}</span>
                        <Link to={pUrl} className="text-xs font-semibold text-zinc-300 group-hover:text-[#ff7a63] inline-flex items-center gap-1 transition-colors">
                          <span>{language === 'EN' ? "Case Study" : "İncele"}</span>
                          <ArrowRight className="w-3 h-3" />
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

    </div>
  );
};

export default SubServiceDetail;
